import { Capacitor } from "@capacitor/core";
import { useConvex } from "convex/react";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { mutationRef } from "@/lib/convex/refs";
import { useDeviceId } from "@/lib/device/DeviceProvider";
import type { PremiumTabId } from "@/lib/monetization/premiumTabs";
import { tabGateStepToScreenId } from "@/lib/monetization/tabGateScreenIds";
import type { PaywallActionOptions } from "@/lib/onboarding/useOnboardingAnalytics";

export type TabPaywallPlatform = "ios" | "android" | "web";

export type TabPaywallEventType =
  | "session_start"
  | "screen_view"
  | "dwell_time"
  | "cta_click"
  | "paywall_action"
  | "permission_outcome"
  | "gate_dismiss"
  | "gate_unlock";

export type TabPaywallAnalyticsEvent = {
  onboardingSessionId: string;
  eventType: TabPaywallEventType;
  step?: number;
  screenId?: string;
  timestamp: number;
  dwellMs?: number;
  ctaKind?: string;
  paywallAction?: string;
  paywallPlan?: "yearly" | "lifetime";
  revenueStatus?: "pending_trial" | "realized";
  price?: number;
  currencyCode?: string;
  priceString?: string;
  productIdentifier?: string;
  trialEndsAtMs?: number;
  intlLocale?: string;
  timezone?: string;
  permissionKind?: string;
  permissionOutcome?: string;
  exitReason?: string;
  metadata?: Record<string, string>;
};

type TrackEventsArgs = {
  anonymousDeviceId: string;
  platform: TabPaywallPlatform;
  appVersion?: string;
  events: TabPaywallAnalyticsEvent[];
};

const trackEventsRef = mutationRef<TrackEventsArgs, { inserted: number }>(
  "onboardingAnalytics:trackEvents",
);

const FLUSH_INTERVAL_MS = 2_000;

function createSessionId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `gate_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function resolvePlatform(): TabPaywallPlatform {
  const platform = Capacitor.getPlatform();
  if (platform === "ios" || platform === "android") return platform;
  return "web";
}

function resolveAppVersion(): string | undefined {
  return import.meta.env.VITE_APP_VERSION?.trim() || undefined;
}

function gateMetadata(requestedTab: PremiumTabId): Record<string, string> {
  return { gateSource: "tab", requestedTab };
}

export function useTabPaywallAnalytics(requestedTab: PremiumTabId) {
  const convex = useConvex();
  const deviceId = useDeviceId();
  const sessionIdRef = useRef(createSessionId());
  const platform = useMemo(() => resolvePlatform(), []);
  const appVersion = useMemo(() => resolveAppVersion(), []);
  const queueRef = useRef<TabPaywallAnalyticsEvent[]>([]);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stepEnteredAtRef = useRef<number>(Date.now());
  const currentStepRef = useRef<number>(1);
  const lastViewedStepRef = useRef<number | null>(null);
  const startedRef = useRef(false);
  const tabMeta = useMemo(() => gateMetadata(requestedTab), [requestedTab]);

  const flush = useCallback(async () => {
    if (!deviceId || queueRef.current.length === 0) return;
    const batch = queueRef.current.splice(0, queueRef.current.length);
    try {
      await convex.mutation(trackEventsRef, {
        anonymousDeviceId: deviceId,
        platform,
        appVersion,
        events: batch,
      });
    } catch (error) {
      console.warn("Tab paywall analytics flush failed", error);
      queueRef.current.unshift(...batch);
    }
  }, [appVersion, convex, deviceId, platform]);

  const scheduleFlush = useCallback(() => {
    if (flushTimerRef.current) return;
    flushTimerRef.current = setTimeout(() => {
      flushTimerRef.current = null;
      void flush();
    }, FLUSH_INTERVAL_MS);
  }, [flush]);

  const enqueue = useCallback(
    (event: Omit<TabPaywallAnalyticsEvent, "onboardingSessionId" | "timestamp"> & { timestamp?: number }) => {
      queueRef.current.push({
        onboardingSessionId: sessionIdRef.current,
        timestamp: event.timestamp ?? Date.now(),
        eventType: event.eventType,
        step: event.step,
        screenId: event.screenId,
        dwellMs: event.dwellMs,
        ctaKind: event.ctaKind,
        paywallAction: event.paywallAction,
        paywallPlan: event.paywallPlan,
        revenueStatus: event.revenueStatus,
        price: event.price,
        currencyCode: event.currencyCode,
        priceString: event.priceString,
        productIdentifier: event.productIdentifier,
        trialEndsAtMs: event.trialEndsAtMs,
        intlLocale: event.intlLocale,
        timezone: event.timezone,
        permissionKind: event.permissionKind,
        permissionOutcome: event.permissionOutcome,
        exitReason: event.exitReason,
        metadata: { ...tabMeta, ...event.metadata },
      });
      scheduleFlush();
    },
    [scheduleFlush, tabMeta],
  );

  const trackSessionStart = useCallback(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    enqueue({ eventType: "session_start" });
  }, [enqueue]);

  const trackScreenView = useCallback(
    (step: number) => {
      if (lastViewedStepRef.current === step) return;

      const previousStep = currentStepRef.current;
      if (previousStep !== step) {
        const dwellMs = Date.now() - stepEnteredAtRef.current;
        if (startedRef.current && lastViewedStepRef.current != null && dwellMs > 0) {
          enqueue({
            eventType: "dwell_time",
            step: previousStep,
            screenId: tabGateStepToScreenId(previousStep),
            dwellMs,
          });
        }
        currentStepRef.current = step;
        stepEnteredAtRef.current = Date.now();
      }

      lastViewedStepRef.current = step;
      enqueue({
        eventType: "screen_view",
        step,
        screenId: tabGateStepToScreenId(step),
      });
    },
    [enqueue],
  );

  const trackCtaClick = useCallback(
    (step: number, ctaKind: "primary" | "secondary" | "skip") => {
      enqueue({
        eventType: "cta_click",
        step,
        screenId: tabGateStepToScreenId(step),
        ctaKind,
      });
    },
    [enqueue],
  );

  const trackPaywallAction = useCallback(
    (paywallAction: string, options?: PaywallActionOptions) => {
      enqueue({
        eventType: "paywall_action",
        step: 3,
        screenId: "paywall",
        paywallAction,
        metadata: options?.metadata,
        paywallPlan: options?.paywallPlan,
        revenueStatus: options?.revenueStatus,
        price: options?.price,
        currencyCode: options?.currencyCode,
        priceString: options?.priceString,
        productIdentifier: options?.productIdentifier,
        trialEndsAtMs: options?.trialEndsAtMs,
        intlLocale: options?.intlLocale,
        timezone: options?.timezone,
      });
    },
    [enqueue],
  );

  const trackPermissionOutcome = useCallback(
    (
      step: number,
      permissionKind: "notifications" | "screen_time" | "vpn",
      permissionOutcome: string,
      metadata?: Record<string, string>,
    ) => {
      enqueue({
        eventType: "permission_outcome",
        step,
        screenId: tabGateStepToScreenId(step),
        permissionKind,
        permissionOutcome,
        metadata,
      });
    },
    [enqueue],
  );

  const trackGateDismiss = useCallback(
    (exitReason: string, step: number) => {
      enqueue({
        eventType: "gate_dismiss",
        step,
        screenId: tabGateStepToScreenId(step),
        exitReason,
      });
      void flush();
    },
    [enqueue, flush],
  );

  const trackGateUnlock = useCallback(() => {
    enqueue({
      eventType: "gate_unlock",
      step: 3,
      screenId: "paywall",
    });
    void flush();
  }, [enqueue, flush]);

  useEffect(() => {
    if (!deviceId) return;
    trackSessionStart();
  }, [deviceId, trackSessionStart]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        void flush();
      }
    };
    const onPageHide = () => {
      void flush();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      void flush();
    };
  }, [flush]);

  return {
    sessionId: sessionIdRef.current,
    requestedTab,
    trackScreenView,
    trackCtaClick,
    trackPaywallAction,
    trackPermissionOutcome,
    trackGateDismiss,
    trackGateUnlock,
    flush,
  };
}

export type TabPaywallAnalyticsApi = ReturnType<typeof useTabPaywallAnalytics>;
